import { createClientSharedComposable, getStore } from '@global-store/store'
import type {
  ConcreteToastOptions,
  Toast,
  ToastOptions,
  ToastType,
  ToastsStore,
} from '@global-store/stores/toast/types'
import { useCookieStorage } from 'internal/composables'
import { randomId } from 'internal/helper'
import { defineStore } from 'pinia'

const getId = (toastOrId: string | Toast) =>
  typeof toastOrId === 'string' ? toastOrId : toastOrId.id

const defaultOptions: ToastOptions = {
  duration: 6000,
  position: 'top-center',
}

export const useToastsStore = createClientSharedComposable(
  (globalOptions: Partial<ToastOptions> = {}) =>
    defineStore('toasts', {
      state: (): ToastsStore => ({
        toasts: useCookieStorage('Toasts', []),
      }),

      getters: {
        displayToasts: (state) =>
          state.toasts.filter((toast) => !toast.options.renderAfterPageReload),
      },

      actions: {
        generateToastId(): string {
          const newId = randomId()

          if (this.toasts.some((toast) => toast.id === newId)) {
            return this.generateToastId()
          }

          return newId
        },

        setToasts(toasts: Toast[]) {
          this.toasts = toasts
        },

        addToast(
          type: ToastType,
          content: string,
          toastOptions: ToastOptions = {}
        ) {
          const options = {
            ...defaultOptions,
            ...globalOptions,
            ...toastOptions,
          }

          this.toasts = [
            ...this.toasts,
            {
              id: this.generateToastId(),
              type,
              content,
              options,
            },
          ]
        },
        addWarningToast(
          content: string,
          toastOptions: ConcreteToastOptions = {}
        ) {
          this.addToast('warning', content, toastOptions)
        },
        addSuccessToast(
          content: string,
          toastOptions: ConcreteToastOptions = {}
        ) {
          this.addToast('success', content, toastOptions)
        },
        addErrorToast(
          content: string,
          toastOptions: ConcreteToastOptions = {}
        ) {
          this.addToast('error', content, toastOptions)
        },
        addInfoToast(content: string, toastOptions: ConcreteToastOptions = {}) {
          this.addToast('info', content, toastOptions)
        },

        updateToast(updateToast: Toast) {
          this.toasts = this.toasts.map((toast) =>
            toast.id === updateToast.id ? updateToast : toast
          )
        },

        removeToast(toastOrId: string | Toast) {
          const id = getId(toastOrId)

          this.toasts = this.toasts.filter((toast) => id !== toast.id)
        },
        removeToasts() {
          this.toasts = []
        },
      },

      hydrate: (storeState) => {
        // @ts-expect-error: https://github.com/microsoft/TypeScript/issues/43826
        storeState.toasts = useCookieStorage<Toast[]>('Toasts', [])
      },
    })(getStore())
)
